import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api';
import InterviewScheduleForm from './InterviewScheduleForm';
import { emptyInterviewSchedule, fromDatetimeLocal, toDatetimeLocal } from '../constants/interview';

function scheduleFromApp(sched) {
  return emptyInterviewSchedule({
    ...sched,
    scheduledAtLocal: toDatetimeLocal(
      sched.status === 'reschedule_requested'
        ? sched.rescheduleRequest?.proposedScheduledAt || sched.scheduledAt
        : sched.scheduledAt
    )
  });
}

function defaultResolution(sched) {
  if (sched.status === 'reschedule_requested') return 'confirm_reschedule';
  return 'update_schedule';
}

export default function InterviewStaffReply({ app, onUpdated }) {
  const qc = useQueryClient();
  const sched = app.interviewSchedule || {};
  const [message, setMessage] = useState('');
  const [resolution, setResolution] = useState(() => defaultResolution(sched));
  const [schedule, setSchedule] = useState(() => scheduleFromApp(sched));
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const s = app.interviewSchedule || {};
    setResolution(defaultResolution(s));
    setSchedule(scheduleFromApp(s));
    setError('');
    if (s.status !== 'reschedule_requested') {
      setSuccess('');
    }
  }, [
    app.id,
    app.interviewSchedule?.status,
    app.interviewSchedule?.updatedAt,
    app.interviewSchedule?.scheduledAt,
    app.rescheduleApprovalSummary?.allApproved
  ]);

  const mutation = useMutation({
    mutationFn: payload =>
      api.post(`/applications/${app.id}/interview-reply`, payload).then(r => r.data),
    onSuccess: data => {
      setMessage('');
      setError('');
      const updatedSched = data?.interviewSchedule || {};
      setResolution(defaultResolution(updatedSched));
      setSchedule(scheduleFromApp(updatedSched));
      setSuccess(
        updatedSched.status === 'reschedule_requested'
          ? 'Saved. Waiting for remaining approvals before you can confirm the proposed time.'
          : 'Interview schedule updated and candidate notified.'
      );
      setExpanded(updatedSched.status === 'reschedule_requested');
      qc.invalidateQueries({ queryKey: ['applications'] });
      qc.invalidateQueries({ queryKey: ['myApplications'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      onUpdated?.(data);
    },
    onError: err => {
      setSuccess('');
      setError(err.response?.data?.message || 'Failed to update interview');
    }
  });

  if (app.stage !== 'interview' || !app.interviewSchedule) return null;
  if (app.interviewSchedule.status === 'cancelled') return null;

  const approvalSummary = app.rescheduleApprovalSummary;
  const awaitingApprovals =
    sched.status === 'reschedule_requested' &&
    approvalSummary &&
    !approvalSummary.allApproved;

  const buildSchedulePayload = () => {
    const iso = fromDatetimeLocal(schedule.scheduledAtLocal);
    const { scheduledAtLocal, ...rest } = schedule;
    return { ...rest, scheduledAt: iso || sched.scheduledAt };
  };

  const submit = () => {
    setSuccess('');
    mutation.mutate({
      resolution,
      message: message.trim(),
      proposedSchedule: buildSchedulePayload()
    });
  };

  if (!expanded && sched.status !== 'reschedule_requested') {
    return (
      <div className="border border-[hsl(var(--ds-primary)/0.2)] bg-[hsl(var(--ds-primary)/0.06)]/30 p-3">
        {success && <p className="text-xs text-green-700 mb-2">{success}</p>}
        <button
          type="button"
          className="text-sm text-[hsl(var(--ds-primary))] hover:underline"
          onClick={() => setExpanded(true)}
        >
          Edit interview schedule
        </button>
      </div>
    );
  }

  return (
    <div className="border border-[hsl(var(--ds-primary)/0.2)] bg-[hsl(var(--ds-primary)/0.06)]/30 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-[hsl(var(--ds-fg))]">Manage interview schedule</p>
        {sched.status !== 'reschedule_requested' && (
          <button
            type="button"
            className="text-xs text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))] shrink-0"
            onClick={() => setExpanded(false)}
          >
            Collapse
          </button>
        )}
      </div>
      {success && <p className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-1.5">{success}</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}
      {sched.status === 'reschedule_requested' && (
        <p className="text-xs text-[hsl(var(--ds-warning-fg))] bg-[hsl(var(--ds-warning)/0.12)] border border-[hsl(var(--ds-warning)/0.35)] px-2 py-1.5">
          Candidate requested a new time. Admin, recruiter, and client must approve in the panel above.
          After all approve, use &quot;Confirm proposed time&quot;. To set a different time without waiting,
          choose &quot;Set new time (staff override)&quot;.
        </p>
      )}
      <div className="field-with-label">
        <select
          className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
          value={resolution}
          onChange={e => setResolution(e.target.value)}
        >
          {sched.status === 'reschedule_requested' && (
            <option value="confirm_reschedule" disabled={awaitingApprovals}>
              Confirm proposed time
              {awaitingApprovals ? ' (pending approvals)' : ''}
            </option>
          )}
          {sched.status === 'reschedule_requested' && (
            <option value="reject_reschedule">Decline reschedule request</option>
          )}
          {sched.status === 'reschedule_requested' && (
            <option value="update_schedule">Set new time (staff override)</option>
          )}
          {sched.status !== 'reschedule_requested' && (
            <option value="update_schedule">Update / send new interview time</option>
          )}
        </select>
        <label>Action</label>
      </div>
      <InterviewScheduleForm
        value={schedule}
        onChange={setSchedule}
        clientContacts={app.clientContacts || []}
        clientAddresses={app.clientAddresses || []}
      />
      <div className="field-with-label">
        <textarea
          className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
          rows={2}
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder=" "
        />
        <label>Message to candidate (optional)</label>
      </div>
      {awaitingApprovals && resolution === 'confirm_reschedule' && (
        <p className="text-xs text-amber-800">
          Waiting for approval from: {approvalSummary.pendingLabels.join(', ')}.
        </p>
      )}

      <button
        type="button"
        className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-3 py-1.5 text-sm disabled:opacity-60"
        disabled={mutation.isLoading || (resolution === 'confirm_reschedule' && awaitingApprovals)}
        onClick={submit}
      >
        {mutation.isLoading
          ? 'Saving…'
          : resolution === 'reject_reschedule'
            ? 'Decline & notify'
            : resolution === 'confirm_reschedule'
              ? 'Confirm & notify candidate'
              : 'Update & notify candidate'}
      </button>
    </div>
  );
}
